import React from "react";
import { createPortal } from "react-dom";
import ModalShell from "./ModalShell";
import { useOverlayBehavior } from "./useOverlayBehavior";

/**
 * Modal — Warm Precision design-system primitive that owns the backdrop,
 * portal, Esc-to-close, backdrop-click-to-close, focus, and body-scroll
 * lock around a <ModalShell>. Pages should stop hand-rolling
 * `fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4`
 * wrappers — that pattern used to be duplicated across ~60 files and each
 * copy had to be individually kept in sync (backdrop opacity, z-index,
 * Esc handling, focus restore) whenever the design changed.
 *
 * Composes ModalShell's existing Header/Body/Footer — Modal only owns the
 * overlay behavior, ModalShell still owns the visual shell. Shares its
 * Esc/focus/scroll-lock behavior with <Drawer> via useOverlayBehavior.
 *
 * Usage:
 *   <Modal open={open} onClose={close} size="md">
 *     <ModalShell.Header title="Edit campaign" onClose={close} />
 *     <ModalShell.Body>…</ModalShell.Body>
 *     <ModalShell.Footer>
 *       <SecondaryButton onClick={close}>Cancel</SecondaryButton>
 *       <PrimaryButton onClick={save}>Save</PrimaryButton>
 *     </ModalShell.Footer>
 *   </Modal>
 *
 * For a critical confirmation where an accidental backdrop click or Esc
 * should NOT dismiss (e.g. mid-destructive-action), pass
 * closeOnBackdropClick={false} closeOnEsc={false}.
 *
 * Need a side-docked sliding panel instead of a centered card? Use <Drawer>
 * — same overlay contract, different visual shell.
 */

type Size = "sm" | "md" | "lg" | "xl" | "2xl" | "5xl";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: Size;
  height?: "auto" | "tall" | "full";
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  className?: string;
  shellClassName?: string;
  /** Forwarded to ModalShell's root — use when there's no visible <ModalShell.Header title> to label the dialog. */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  children?: React.ReactNode;
}

function Modal({
  open,
  onClose,
  size = "md",
  height = "auto",
  closeOnEsc = true,
  closeOnBackdropClick = true,
  className = "",
  shellClassName = "",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
}: ModalProps) {
  const shellRef = useOverlayBehavior(open, onClose, { closeOnEsc });

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${className}`}
      onMouseDown={(e) => {
        if (closeOnBackdropClick && e.target === e.currentTarget) onClose();
      }}
    >
      <ModalShell
        ref={shellRef}
        size={size}
        height={height}
        className={shellClassName}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {children}
      </ModalShell>
    </div>,
    document.body
  );
}

export default Modal;
